import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { addNotice, editNotice, getNoticeDetail } from "common-lib";
import PageShell from "../../components/new/PageShell";
import NoticeEditor from "../../components/new/NoticeEditor";
import { cleanupNoticeImages } from "../../services/noticeService";
import { isEmptyHtml } from "../../utils/richText";

export default function NoticeFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  // 이 세션에서 업로드한 이미지 URL 추적 (취소/저장 시 미사용분 정리)
  const uploadedImagesRef = useRef<string[]>([]);

  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");

  useEffect(() => {
    if (!id) return;
    const fetchDetail = async () => {
      try {
        const response = await getNoticeDetail(Number(id));
        setTitle(response.data.title);
        setContent(response.data.content);
      } catch (error) {
        console.error("공지 상세 조회 실패:", error);
      }
    };
    fetchDetail();
  }, [id]);

  const leave = () => {
    // 올렸지만 어떤 공지에도 저장되지 않은 이미지는 서버에서 정리 (참조 중인 건 백엔드가 보존)
    cleanupNoticeImages(uploadedImagesRef.current);
    navigate("/main/notices");
  };

  const handleSave = async () => {
    if (!title.trim() || isEmptyHtml(content)) {
      toast.warn(t("EnterTitleAndContentMsg"));
      return;
    }
    setIsSaving(true);
    try {
      if (id) {
        await editNotice(Number(id), { title, content, user_no: userInfo.id }, userInfo.id);
        toast.success(t("NoticeUpdatedMsg"));
      } else {
        await addNotice({ title, content, user_no: userInfo.id });
        toast.success(t("NoticeCreatedMsg"));
      }
      leave();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("NoticeSaveFailedMsg"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageShell
      title={id ? t("EditNotice") : t("CreateNotice")}
      subtitle={t("NoticeSubtitle")}
      actions={
        <button
          onClick={leave}
          className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition flex items-center gap-1.5"
        >
          <ArrowLeft size={16} />
          {t("List")}
        </button>
      }
    >
      <div className="flex flex-col gap-1 mb-5">
        <label className="text-xs font-medium text-gray-500">{t("NoticeTitle")}</label>
        <input
          type="text"
          value={title}
          // 백엔드 notice.title 이 VARCHAR(50)
          maxLength={50}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("EnterNoticeTitle")}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-500">{t("NoticeContent")}</label>
        <NoticeEditor
          value={content}
          onChange={setContent}
          placeholder={t("EnterNoticeContent")}
          onImageUploaded={(url) => uploadedImagesRef.current.push(url)}
        />
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <button onClick={leave} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition">
          {t("Cancel")}
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-5 py-2 text-sm rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition disabled:opacity-50"
        >
          {id ? t("Edit") : t("Register")}
        </button>
      </div>
    </PageShell>
  );
}
